import * as React from 'react'
import { Button as AntButton } from 'antd'
import type { ButtonProps as AntButtonProps } from 'antd'
import { cn } from '@/lib/utils'

// 保持与原 shadcn/ui Button 相同的 variant 和 size API
export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  asChild?: boolean
  loading?: boolean
  icon?: React.ReactNode
  htmlType?: 'button' | 'submit' | 'reset'
}

// 映射 variant 到 Ant Design 的 type
function mapVariantToAnt(variant: ButtonProps['variant']): AntButtonProps['type'] {
  switch (variant) {
    case 'default':
      return 'primary'
    case 'destructive':
      return 'primary' // Will add danger prop
    case 'outline':
      return 'default'
    case 'secondary':
      return 'default'
    case 'ghost':
      return 'text'
    case 'link':
      return 'link'
    default:
      return 'primary'
  }
}

// 映射 size 到 Ant Design 的 size
function mapSizeToAnt(size: ButtonProps['size']): AntButtonProps['size'] {
  switch (size) {
    case 'sm':
      return 'small'
    case 'lg':
      return 'large'
    case 'icon':
      return 'middle'
    default:
      return 'middle'
  }
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      asChild = false,
      loading,
      icon,
      children,
      disabled,
      htmlType = 'button',
      ...props
    },
    ref
  ) => {
    const antType = mapVariantToAnt(variant)
    const antSize = mapSizeToAnt(size)
    const isDanger = variant === 'destructive'

    // 特殊处理 icon-only button
    const isIconOnly = size === 'icon'

    return (
      <AntButton
        ref={ref}
        type={antType}
        size={antSize}
        loading={loading}
        icon={icon}
        disabled={disabled}
        htmlType={htmlType}
        danger={isDanger}
        className={cn(
          // 基础样式覆盖
          'inline-flex items-center justify-center font-medium',
          'transition-all duration-200',
          // icon-only 样式
          isIconOnly && 'h-9 w-9 p-0 min-w-0',
          // ghost 样式
          variant === 'ghost' && 'hover:bg-gray-100',
          className
        )}
        {...(props as any)}
      >
        {children}
      </AntButton>
    )
  }
)
Button.displayName = 'Button'

// 保留 buttonVariants 导出以兼容现有代码
const buttonVariants = () => ''

export { Button, buttonVariants }
